import { useState } from 'react';
import { CheckCircle, Zap, Users, Eye, DollarSign, Cog, TrendingUp, Clock, Award, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
const InforCPQShowcase = () => {
  const features = [
    {
      icon: Cog,
      title: 'Visual Configuration',
      description: 'Real-time 2D/3D rendering of products as they are configured',
      color: '#3B82F6'
    },
    {
      icon: Zap,
      title: 'Guided Selling',
      description: 'Walk through questions to arrive at the best configuration',
      color: '#10B981'
    },
    {
      icon: DollarSign,
      title: 'Automated Pricing',
      description: 'Real-time pricing based on configuration and customer rules',
      color: '#F59E0B'
    },
    {
      icon: Users,
      title: 'Seamless Integration',
      description: 'Connects with CRM, ERP, and e-commerce platforms',
      color: '#8B5CF6'
    }
  ];

  const benefits = [
    { metric: '70-90%', description: 'Reduction in quote-to-order cycle time' },
    { metric: '40%', description: 'Fewer quoting and order errors' },
    { metric: '10 min', description: 'Quote generation time (vs. days)' },
    { metric: '100%', description: 'Configuration accuracy guarantee' }
  ];

  const integrations = [
    'Infor CRM - Track opportunities and engagement',
    'Infor ERP - CloudSuite Industrial integration',
    'Rhythm for Commerce - Customer self-service',
    'CAD/PLM Systems - Generate drawings and BOMs'
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAFC' }}>
      {/* Hero Section */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #011673 0%, #8e25f8 100%)' }}>
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}></div>
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div style={{ color: '#FFFFFF' }}>
              <div className="inline-flex items-center rounded-full px-4 py-2 mb-6" 
                   style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(8px)' }}>
                <Award className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Enterprise CPQ Solution</span>
              </div>
              <h1 className="text-5xl font-bold mb-6 leading-tight">
                Infor CPQ
                <span className="block text-3xl font-normal mt-2" style={{ opacity: '0.9' }}>
                  Empowering Sales at PTKCS
                </span>
              </h1>
              <p className="text-xl leading-relaxed mb-8 text-white" style={{ opacity: '0.9' }}>
                Transform complex sales processes with intelligent configuration, automated pricing, 
                and visual selling capabilities that delight customers and empower teams.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                 <button 
                  className="px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-all duration-200 flex items-center"
                  style={{ backgroundColor: '#FFFFFF', color: '#7C3AED' }}
                >
                 
                  Get Started <ArrowRight className="w-4 h-4 ml-2" />
                </button>
                
                </Link>
               
               
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl p-8" 
                   style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(8px)' }}>
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop&crop=center"
                  alt="CPQ Dashboard"
                  className="w-full h-64 object-cover rounded-lg"
                />
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="rounded-lg p-4 text-center" 
                       style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(8px)' }}>
                    <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>10x</div>
                    <div className="text-sm" style={{ color: '#FFFFFF', opacity: '0.8' }}>Faster Quotes</div>
                  </div>
                  <div className="rounded-lg p-4 text-center"
                       style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(8px)' }}>
                    <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Zero</div>
                    <div className="text-sm" style={{ color: '#FFFFFF', opacity: '0.8' }}>Re-entry Errors</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center rounded-full px-4 py-2 mb-6"
                 style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
              <Eye className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Overview</span>
            </div>
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#111827' }}>
              What Is Infor CPQ?
            </h2>
            <p className="text-xl leading-relaxed" style={{ color: '#6B7280' }}>
              A powerful, cloud-based solution that enables businesses to sell complex, 
              configurable products more efficiently. Perfect for companies dealing with 
              customizable, engineered-to-order, or multi-option products.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="rounded-2xl p-8" 
                 style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)' }}>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6"
                   style={{ backgroundColor: '#2563EB' }}>
                <Cog className="w-6 h-6" style={{ color: '#FFFFFF' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#111827' }}>Configure</h3>
              <p style={{ color: '#6B7280' }}>Visual and rules-based product configuration with real-time validation and instant feedback</p>
            </div>

            <div className="rounded-2xl p-8"
                 style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' }}>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6"
                   style={{ backgroundColor: '#059669' }}>
                <DollarSign className="w-6 h-6" style={{ color: '#FFFFFF' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#111827' }}>Price</h3>
              <p style={{ color: '#6B7280' }}>Automated, accurate pricing with customer-specific rules, discounts, and margin controls</p>
            </div>

            <div className="rounded-2xl p-8"
                 style={{ background: 'linear-gradient(135deg, #FAF5FF 0%, #E9D5FF 100%)' }}>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6"
                   style={{ backgroundColor: '#7C3AED' }}>
                <Eye className="w-6 h-6" style={{ color: '#FFFFFF' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#111827' }}>Quote</h3>
              <p style={{ color: '#6B7280' }}>Professional quotes with technical specifications, visuals, and customized terms</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-12">
            <div className="text-center">
              <div className="inline-flex items-center rounded-full px-4 py-2 mb-6"
                   style={{ backgroundColor: '#F0F9FF', color: '#0EA5E9' }}>
                <Cog className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Features</span>
              </div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#111827' }}>Powerful Features</h2>
              <p className="text-xl max-w-2xl mx-auto" style={{ color: '#6B7280' }}>
                Discover how PTKCS leverages cutting-edge CPQ capabilities to transform the sales experience
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="rounded-2xl p-8 hover:opacity-95 transition-opacity duration-300"
                       style={{ backgroundColor: '#F8FAFC' }}>
                    <div className="flex items-start space-x-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: feature.color + '20' }}
                      >
                        <Icon className="w-6 h-6" style={{ color: feature.color }} />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-3" style={{ color: '#111827' }}>{feature.title}</h3>
                        <p className="leading-relaxed" style={{ color: '#6B7280' }}>{feature.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-2xl p-8" style={{ background: 'linear-gradient(135deg, #111827 0%, #374151 100%)' }}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Real-World Example</h3>
                  <p className="leading-relaxed mb-6" style={{ color: '#D1D5DB' }}>
                    A PTKCS sales rep designs a custom industrial machine with a client. Using Infor CPQ, 
                    they open a guided configuration tool, input requirements, and see a rendered image 
                    instantly. The system generates pricing, BOMs, and quotes in under 10 minutes.
                  </p>
                  <div className="flex items-center" style={{ color: '#60A5FA' }}>
                    <Clock className="w-5 h-5 mr-2" />
                    <span className="font-medium">Previously took days - now takes minutes</span>
                  </div>
                </div>
                <div>
                  <img 
                    src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=500&h=300&fit=crop&crop=center"
                    alt="Industrial Machine Configuration"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-12">
            <div className="text-center">
              <div className="inline-flex items-center rounded-full px-4 py-2 mb-6"
                   style={{ backgroundColor: '#F0FDF4', color: '#059669' }}>
                <TrendingUp className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Benefits</span>
              </div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#111827' }}>Business Benefits</h2>
              <p className="text-xl max-w-2xl mx-auto" style={{ color: '#6B7280' }}>
                See the measurable impact Infor CPQ delivers for PTKCS operations and customer experience
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center rounded-2xl p-8" 
                     style={{ backgroundColor: '#FFFFFF' }}>
                  <div className="text-4xl font-bold mb-3" style={{ color: '#2563EB' }}>{benefit.metric}</div>
                  <div className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{benefit.description}</div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl p-12" 
                 style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)' }}>
              <h3 className="text-3xl font-bold mb-8 text-center" style={{ color: '#111827' }}>Strategic Differentiation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  { title: 'Speed', desc: 'Rapid response times that exceed customer expectations' },
                  { title: 'Accuracy', desc: 'Precise configuration and pricing eliminating errors' },
                  { title: 'Consistency', desc: 'Uniform customer experience across all touchpoints' },
                  { title: 'Innovation', desc: 'Visual selling and self-service capabilities' }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                         style={{ backgroundColor: '#2563EB' }}>
                      <CheckCircle className="w-10 h-10" style={{ color: '#FFFFFF' }} />
                    </div>
                    <h4 className="text-lg font-semibold mb-2" style={{ color: '#111827' }}>{item.title}</h4>
                    <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="rounded-2xl p-6" style={{ backgroundColor: '#FFFFFF' }}>
                <h4 className="text-lg font-semibold mb-3" style={{ color: '#111827' }}>Sales Excellence</h4>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>Empower sales teams with tools that make complex selling simple and efficient</p>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm" style={{ color: '#6B7280' }}>
                    <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: '#10B981' }}></div>
                    Guided selling workflows
                  </li>
                  <li className="flex items-center text-sm" style={{ color: '#6B7280' }}>
                    <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: '#10B981' }}></div>
                    Real-time product visualization
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl p-6" style={{ backgroundColor: '#FFFFFF' }}>
                <h4 className="text-lg font-semibold mb-3" style={{ color: '#111827' }}>Customer Delight</h4>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>Create exceptional buying experiences that build lasting relationships</p>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm" style={{ color: '#6B7280' }}>
                    <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: '#F59E0B' }}></div>
                    Interactive configuration
                  </li>
                  <li className="flex items-center text-sm" style={{ color: '#6B7280' }}>
                    <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: '#F59E0B' }}></div>
                    Instant quote generation
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl p-6" style={{ backgroundColor: '#FFFFFF' }}>
                <h4 className="text-lg font-semibold mb-3" style={{ color: '#111827' }}>Operational Efficiency</h4>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>Streamline processes and eliminate manual work across the organization</p>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm" style={{ color: '#6B7280' }}>
                    <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: '#8B5CF6' }}></div>
                    Automated pricing rules
                  </li>
                  <li className="flex items-center text-sm" style={{ color: '#6B7280' }}>
                    <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: '#8B5CF6' }}></div>
                    Integrated workflows
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Integration Section */}
      <div className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-12">
            <div className="text-center">
              <div className="inline-flex items-center rounded-full px-4 py-2 mb-6"
                   style={{ backgroundColor: '#FDF4FF', color: '#7C3AED' }}>
                <Users className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Integration</span>
              </div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#111827' }}>Seamless Integration</h2>
              <p className="text-xl max-w-2xl mx-auto" style={{ color: '#6B7280' }}>
                Infor CPQ connects across PTKCS's entire digital selling ecosystem for end-to-end workflow automation
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-6">
                {integrations.map((integration, index) => (
                  <div key={index} className="flex items-start space-x-4 p-6 rounded-xl hover:opacity-95 transition-opacity duration-200"
                       style={{ backgroundColor: '#F8FAFC' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                         style={{ backgroundColor: '#D1FAE5' }}>
                      <CheckCircle className="w-6 h-6" style={{ color: '#059669' }} />
                    </div>
                    <div>
                      <div className="font-medium text-lg mb-1" style={{ color: '#111827' }}>
                        {integration.split(' - ')[0]}
                      </div>
                      <div style={{ color: '#6B7280' }}>{integration.split(' - ')[1]}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl p-8"
                   style={{ background: 'linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)' }}>
                <h3 className="text-2xl font-semibold mb-6" style={{ color: '#111827' }}>Integration Benefits</h3>
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop&crop=center"
                  alt="Integration Workflow"
                  className="w-full h-40 object-cover rounded-lg mb-6"
                />
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-3 h-3 rounded-full mt-1 mr-3" style={{ backgroundColor: '#7C3AED' }}></div>
                    <div>
                      <div className="font-medium mb-1" style={{ color: '#111827' }}>Zero re-entry errors</div>
                      <p className="text-sm" style={{ color: '#6B7280' }}>Data flows seamlessly between systems without manual intervention</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 rounded-full mt-1 mr-3" style={{ backgroundColor: '#7C3AED' }}></div>
                    <div>
                      <div className="font-medium mb-1" style={{ color: '#111827' }}>Complete configuration data flow</div>
                      <p className="text-sm" style={{ color: '#6B7280' }}>Full product specifications transfer to manufacturing systems</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-3 h-3 rounded-full mt-1 mr-3" style={{ backgroundColor: '#7C3AED' }}></div>
                    <div>
                      <div className="font-medium mb-1" style={{ color: '#111827' }}>Ready for production approval</div>
                      <p className="text-sm" style={{ color: '#6B7280' }}>Instant handoff to engineering and manufacturing teams</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-8" style={{ background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)' }}>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#111827' }}>Enterprise Ecosystem</h3>
                <p style={{ color: '#6B7280' }}>Infor CPQ integrates with your existing technology stack</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {['CRM Systems', 'ERP Platforms', 'E-commerce', 'Manufacturing'].map((system, index) => (
                  <div key={index} className="text-center p-4 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                    <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                         style={{ backgroundColor: '#0EA5E9' }}>
                      <Users className="w-6 h-6" style={{ color: '#FFFFFF' }} />
                    </div>
                    <div className="font-medium text-sm" style={{ color: '#111827' }}>{system}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20" style={{ backgroundColor: '#111827' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6" style={{ color: '#FFFFFF' }}>Ready to Transform Your Sales Process?</h2>
          <p className="text-xl mb-8" style={{ color: '#D1D5DB' }}>
            Join PTKCS in delivering exceptional customer experiences with the power of Infor CPQ
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: '#2563EB', color: '#FFFFFF' }}>
              Get Started Today
            </button>
            <button className="px-8 py-4 rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: 'transparent', color: '#FFFFFF', border: '2px solid #FFFFFF' }}>
              Schedule Demo
            </button>
          </div>
        </div>
      </div>

      
     
    </div>
  );
};

export default InforCPQShowcase;